import type { IntlShape } from 'react-intl';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { SYNC_QUEUE_STATUSES, type SyncQueueRecord } from '@/shared/db/schema';

export const STALE_SYNCING_RECORD_MS = 60_000;

export function createIdempotencyKey(session: Pick<CaptureSession, 'id' | 'workflowType' | 'createdAt'>) {
  return `${session.workflowType}:${session.id}:${session.createdAt}`;
}

export function readSessionLocationApiKey(session: CaptureSession) {
  if (session.locationApiKey?.trim()) {
    return session.locationApiKey.trim();
  }

  throw new Error(`Location API key is required before queueing session ${session.id}.`);
}

export function isPendingSyncQueueRecord(record: SyncQueueRecord) {
  return record.status === SYNC_QUEUE_STATUSES.queued;
}

export function isRetryableSyncQueueRecord(record: SyncQueueRecord) {
  return (
    record.status === SYNC_QUEUE_STATUSES.syncFailed ||
    record.status === SYNC_QUEUE_STATUSES.needsReview ||
    record.status === SYNC_QUEUE_STATUSES.conflict
  );
}

export function isReviewSyncQueueStatus(status: string) {
  return status === SYNC_QUEUE_STATUSES.needsReview || status === SYNC_QUEUE_STATUSES.conflict;
}

const syncErrorMessageIds = new Map([
  ['METER_LOOKUP_FAILED', 'meterLookupFailed'],
  ['METER_IDENTITY_CONFLICT', 'meterIdentityConflict'],
  ['METER_API_KEY_ALREADY_EXISTS', 'duplicateApiKey'],
  ['NETWORK_OR_TRANSPORT_ERROR', 'connection'],
  ['SYNC_INTERRUPTED', 'interrupted'],
  ['LOCATION_API_KEY_MISSING', 'locationAccess'],
  ['HTTP_401', 'locationAccess'],
  ['HTTP_403', 'locationAccess'],
  ['SESSION_NOT_FOUND', 'missingRecord'],
  ['WORKFLOW_NOT_SUPPORTED', 'unsupportedRecord'],
]);
const genericHttpErrorMessages = new Set([
  'bad request',
  'not found',
  'method not allowed',
  'request timeout',
  'conflict',
  'payload too large',
  'request entity too large',
  'unsupported media type',
  'unprocessable entity',
  'unprocessable content',
  'too many requests',
]);

export function readSyncErrorMessage(
  input: {
    errorCode?: string;
    session?: Pick<CaptureSession, 'validationErrors'>;
    status: string;
  },
  intl: IntlShape,
) {
  if (
    !isReviewSyncQueueStatus(input.status) &&
    input.status !== SYNC_QUEUE_STATUSES.syncFailed &&
    input.status !== 'failed'
  ) {
    return undefined;
  }

  const knownMessageId = syncErrorMessageIds.get(input.errorCode ?? '');
  if (knownMessageId) {
    return intl.formatMessage({ id: `app.inventory.sync.error.${knownMessageId}` });
  }
  if (/^HTTP_5\d{2}$/.test(input.errorCode ?? '')) {
    return intl.formatMessage({ id: 'app.inventory.sync.error.serverUnavailable' });
  }

  const validationMessage = isReviewSyncQueueStatus(input.status)
    ? input.session?.validationErrors
        .filter(
          issue =>
            issue.fieldId ||
            issue.blockId ||
            !/^HTTP_\d{3}$/.test(issue.code) ||
            !genericHttpErrorMessages.has(issue.message.trim().toLowerCase()),
        )
        .map(issue => {
          const messageId = syncErrorMessageIds.get(issue.code);
          return messageId ? intl.formatMessage({ id: `app.inventory.sync.error.${messageId}` }) : issue.message.trim();
        })
        .filter(message => message && !/^[A-Z][A-Z0-9_]*$/.test(message))
        .join(' · ')
    : undefined;

  if (validationMessage) {
    return validationMessage;
  }

  let messageId = 'unknown';
  if (input.errorCode === 'PARTIAL_SYNC') {
    messageId = 'partial';
  } else if (
    input.status === SYNC_QUEUE_STATUSES.conflict ||
    input.errorCode === 'CONFLICT' ||
    input.errorCode === 'HTTP_409'
  ) {
    messageId = 'conflict';
  } else if (
    input.status === SYNC_QUEUE_STATUSES.needsReview ||
    input.errorCode === 'SYNC_REJECTED' ||
    /^HTTP_4\d{2}$/.test(input.errorCode ?? '')
  ) {
    messageId = 'rejected';
  }

  return intl.formatMessage({ id: `app.inventory.sync.error.${messageId}` });
}

export function sortSyncQueueRecordsOldestFirst(records: SyncQueueRecord[]) {
  return records.toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export function sortSyncQueueRecordsNewestUpdateFirst(records: SyncQueueRecord[]) {
  return records.toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function isStaleSyncingRecord(record: SyncQueueRecord, nowTime: number, staleAfterMs: number) {
  if (record.status !== SYNC_QUEUE_STATUSES.syncing) {
    return false;
  }

  const updatedTime = Date.parse(record.updatedAt);

  return !(Number.isFinite(updatedTime) && Number.isFinite(nowTime)) || nowTime - updatedTime >= staleAfterMs;
}
